import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Loader2, RefreshCw } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';

export interface TurnstileCaptchaHandle {
  reset: () => void;
}

interface TurnstileCaptchaProps {
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  enabled?: boolean;
  siteKey?: string;
}

/**
 * Fallback component when Turnstile crashes (e.g., script not loaded)
 * Shows a warning but allows the user to continue
 */
const TurnstileFallback = ({ resetErrorBoundary }: { resetErrorBoundary: () => void }) => {
  return (
    <div className="space-y-3">
      <Alert variant="destructive" className="border-orange-500/50 bg-orange-500/10">
        <AlertCircle className="h-4 w-4 text-orange-500" />
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full ml-2">
          <AlertDescription className="text-orange-600 dark:text-orange-400">
            Verificação de segurança indisponível. Tente recarregar a página.
          </AlertDescription>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.location.reload()}
              className="h-8 gap-1 border-orange-500/30 text-orange-600 hover:bg-orange-500/20"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Recarregar
            </Button>
            <button 
              onClick={resetErrorBoundary}
              className="text-xs underline hover:no-underline text-orange-600/70"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </Alert>
    </div>
  );
};

const TurnstileCaptchaInner = forwardRef<TurnstileCaptchaHandle, TurnstileCaptchaProps>(({ 
  onSuccess, 
  onError, 
  onExpire, 
  enabled = true,
  siteKey = '0x4AAAAAAB-eeAgUtKF8S5MQ'
}, ref) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'idle'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isScriptReady, setIsScriptReady] = useState(false);
  const mountedRef = useRef(true);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  // Expor método reset() para o componente pai gerar novo token após cada submit
  useImperativeHandle(ref, () => ({
    reset: () => {
      try {
        turnstileRef.current?.reset();
        setStatus('idle');
        setErrorMessage('');
      } catch (e) {
        console.warn('[Turnstile] Erro ao resetar widget:', e);
      }
    },
  }), []);

  // Protect against window.turnstile.remove crash on unmount
  useEffect(() => {
    return () => {
      try {
        if (typeof window !== 'undefined' && window.turnstile && typeof window.turnstile.remove !== 'function') {
          console.warn('[Turnstile] window.turnstile.remove não é função, criando stub para evitar crash');
          window.turnstile.remove = () => {};
        }
      } catch (e) {
        console.warn('[Turnstile] Erro ao verificar window.turnstile:', e);
      }
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    console.log('[Turnstile] Componente montado');
    console.log('[Turnstile] Site Key:', siteKey);
    console.log('[Turnstile] Domínio atual:', window.location.hostname);
    
    // Check if Turnstile script is loaded
    const checkScript = () => {
      if (typeof window !== 'undefined' && window.turnstile) {
        setIsScriptReady(true);
      }
    };
    
    // Check immediately
    checkScript();
    
    // Also check after a short delay (script might be loading)
    const timer = setTimeout(checkScript, 500);
    
    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
    };
  }, [siteKey]);

  const handleSuccess = (token: string) => {
    if (!mountedRef.current) return;
    console.log('[Turnstile] CAPTCHA resolvido com sucesso');
    setStatus('success');
    setErrorMessage('');
    onSuccess(token);
  };

  const handleError = (error?: Error | any) => {
    if (!mountedRef.current) return;
    console.error('[Turnstile] Erro no CAPTCHA:', error);
    setStatus('error');
    
    // Erro 400020 significa domínio não autorizado
    if (error?.toString().includes('400020')) {
      setErrorMessage('Domínio não autorizado. Adicione este domínio nas configurações do Cloudflare Turnstile.');
    } else {
      setErrorMessage('Falha ao carregar o CAPTCHA. Verifique sua conexão ou configurações.');
    }
    
    onError?.();
  };

  const handleExpire = () => {
    if (!mountedRef.current) return;
    console.log('[Turnstile] Token expirado');
    setStatus('idle');
    onExpire?.();
  };

  if (!enabled) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-center min-h-[65px]">
        <Turnstile
          ref={turnstileRef}
          siteKey={siteKey}
          onSuccess={handleSuccess}
          onError={handleError}
          onExpire={handleExpire}
          onBeforeInteractive={() => {
            if (!mountedRef.current) return;
            console.log('[Turnstile] Iniciando carregamento...');
            setStatus('loading');
          }}
          onAfterInteractive={() => {
            if (!mountedRef.current) return;
            console.log('[Turnstile] Widget interativo');
            setIsScriptReady(true);
            setStatus('idle');
          }}
          options={{
            theme: 'auto',
            size: 'normal',
            retry: 'auto',
            refreshExpired: 'auto',
          }}
        />
      </div>

      {status === 'success' && (
        <Alert className="border-green-500/50 bg-green-500/10">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription className="text-green-600 dark:text-green-400">
            Verificação concluída com sucesso!
          </AlertDescription>
        </Alert>
      )}

      {status === 'error' && errorMessage && (
        <Alert variant="destructive" className="bg-destructive/15 border-destructive/30">
          <AlertCircle className="h-4 w-4" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full">
            <AlertDescription className="text-sm font-medium">
              {errorMessage}
            </AlertDescription>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.location.reload()}
              className="h-8 gap-2 whitespace-nowrap bg-background/50 hover:bg-background border-destructive/20 text-destructive-foreground"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Recarregar
            </Button>
          </div>
        </Alert>
      )}
    </div>
  );
});

TurnstileCaptchaInner.displayName = 'TurnstileCaptchaInner';

/**
 * TurnstileCaptcha with ErrorBoundary wrapper
 * Prevents "window.turnstile.remove is not a function" crashes
 */
const TurnstileCaptcha = forwardRef<TurnstileCaptchaHandle, TurnstileCaptchaProps>((props, ref) => {
  return (
    <ErrorBoundary
      FallbackComponent={TurnstileFallback}
      onError={(error) => {
        console.error('[Turnstile] ErrorBoundary caught:', error);
        props.onError?.();
      }}
    >
      <TurnstileCaptchaInner {...props} ref={ref} />
    </ErrorBoundary>
  );
});

TurnstileCaptcha.displayName = 'TurnstileCaptcha';

export default TurnstileCaptcha;
